<script setup lang="ts">
import { useGameConfig } from '#imports'
// ==============
// Props
// ==============
interface CombinedJackpotProps {
    jackpot: string | number
    slim?: boolean
    shinyAmount?: boolean
}

withDefaults(defineProps<CombinedJackpotProps>(), {
    slim: false,
})

const { translations } = useGameConfig()
</script>

<template>
    <div class="combined-jackpot prize" :class="{ 'combined-jackpot--slim': slim, 'combined-jackpot--shiny-amount': shinyAmount }">
        <h5 class="combined-jackpot__title">
            {{ translations.combinedJackpot }}
        </h5>
        <h4 class="combined-jackpot__amount">
            <UIAnimatedNumber
                v-if="jackpot"
                :value="jackpot"
                :digit-count="true"
            />
            <UILoader
                v-else
                text=""
                :color="false"
                width="36px"
                height="36px"
            />
        </h4>
    </div>
</template>

<style lang="scss" scoped>
.combined-jackpot {
    width: 100%;
    text-align: center;
    border-radius: $game-tile-border-radius;
    padding: 13px;
    background: $game-combined-jackpot-bg;
    margin-bottom: 10px;
    text-shadow: $game-combined-jackpot-text-shadow;
    min-height: 62px;

    &__title,
    &__amount {
        font-weight: $text-extra-bold;
        align-content: center;
        margin: 0;
    }

    &__title {
        color: $game-combined-jackpot-title-color;
        font-size: 12px;
        padding: 0;
    }

    &__amount {
        position: relative;
        color: $game-combined-jackpot-amount-color;
        font-size: 20px;
        min-height: 20px;
        line-height: 1.1;
        min-width: 80px;
    }

    &--slim {
        display: flex;
        justify-content: center;
        align-items: center;
        align-content: center;
        min-height: 34px;
        padding: 6px 10px;
        margin-bottom: 20px;

        @include tablet {
            flex-direction: column;
        }

        @include desktop {
            padding: 6px 15px;
        }

        .combined-jackpot__title {
            font-size: $font-size-sm;

            @include mobile(sm) {
                font-size: $font-size-lg;
            }

            @include tablet {
                font-size: $font-size-xs;
            }
        }

        .combined-jackpot__amount {
            left: 10px;
            font-size: $font-size-base;

            @include mobile(sm) {
                font-size: $font-size-xl;
            }

            @include tablet {
                left: auto;
                height: 22px;
            }
        }
    }

    &--shiny-amount {
        .combined-jackpot__amount {
            background: $game-combined-jackpot-amount-gradient;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
    }
}

:deep(.loader) {
    color: var(--on-color);
}
</style>
